import { create, useModal } from '@ebay/nice-modal-react';
import Popup from '@shared/components/Popup/Popup';
import { MatrixError } from 'matrix-js-sdk/src/matrix';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import WorkspaceInviteContent from 'modules/Workspaces/WorkspaceInviteContent/WorkspaceInviteContent';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';
import { getViaServers } from 'utils/matrixUtils';

type WorkspaceInviteModalProps = {
  workspaceId: string;
};

const WorkspaceInviteModal = create(({ workspaceId }: WorkspaceInviteModalProps) => {
  const { t } = useTranslation();
  const { remove } = useModal(WorkspaceInviteModal);
  const { matrixClient } = useMatrixContext();
  const { pushToast } = useToast();
  const { setWorkspaceId } = useWorkspacesContext();
  const [isLoading, setIsLoading] = useState(false);

  const workspaceName = matrixClient?.getRoom(workspaceId)?.name || t('workspaces.workspace');

  const handleJoin = async () => {
    setIsLoading(true);
    try {
      await matrixClient.joinRoom(workspaceId, { viaServers: getViaServers(workspaceId, matrixClient) });
      setWorkspaceId(workspaceId);
      pushToast({ variant: 'success', title: t('workspaces.joined_workspace', { name: workspaceName }) });
      remove();
    } catch (error) {
      logger.error(`Failed to join workspace ${workspaceId}`, error);
      if (error instanceof MatrixError) {
        // Server rejected the join — the workspace was likely deleted. Self-leave the
        // stale invite so the existing cleanup chain dismisses the toast/notification,
        // and show a graceful "invite no longer valid" message instead of a raw error.
        await matrixClient.leave(workspaceId).catch((leaveError) => {
          logger.warn(`Failed to leave workspace ${workspaceId} after join rejection`, leaveError);
        });
        pushToast({
          variant: 'warning',
          title: t('workspaces.invite_expired_or_canceled'),
          description: t('workspaces.invite_expired_or_canceled_description'),
        });
      } else {
        // Transient (network/transport) error — keep the invite so the user can retry.
        pushToast({ variant: 'error', title: t('workspaces.join_failed') });
      }
      remove();
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeave = async () => {
    setIsLoading(true);
    await matrixClient.leave(workspaceId).finally(() => {
      setIsLoading(false);
      remove();
    });
  };

  return (
    <Popup isVisible closePopup={remove} wrapperId="workspace-invite-modal">
      <Popup.Container>
        <WorkspaceInviteContent
          workspaceName={workspaceName}
          isLoggedIn={true}
          isLoading={isLoading}
          onJoin={handleJoin}
          onCancel={handleLeave}
          cancelButtonText={t('decline')}
        />
      </Popup.Container>
    </Popup>
  );
});

export default WorkspaceInviteModal;
